import AddIcon from '@mui/icons-material/Add'
import DeleteOutlineIcon from '@mui/icons-material/DeleteOutlined'
import Alert from '@mui/material/Alert'
import Box from '@mui/material/Box'
import Button from '@mui/material/Button'
import DialogActions from '@mui/material/DialogActions'
import DialogContent from '@mui/material/DialogContent'
import DialogTitle from '@mui/material/DialogTitle'
import IconButton from '@mui/material/IconButton'
import MenuItem from '@mui/material/MenuItem'
import TextField from '@mui/material/TextField'
import Typography from '@mui/material/Typography'
import { useState } from 'react'
import { useContractors, useSuppliers, type SupplyScope } from '../../api/execution'
import { useMaterials } from '../../api/hooks'
import { ErrorBlock } from '../../components/Feedback'

interface Line {
  key: number
  materialId: string
  ordered: string
  received: string
  accepted: string
  rejected: string
  rate: string
}

let nextKey = 1
const emptyLine = (): Line => ({
  key: nextKey++,
  materialId: '',
  ordered: '',
  received: '',
  accepted: '',
  rejected: '',
  rate: '',
})

/** An existing delivery being corrected, as the receipt list holds it. */
export interface MaterialReceiptInitial {
  receiptNumber?: string
  supplierId?: string
  supplyScope?: SupplyScope
  contractorId?: string
  challanNumber?: string
  challanDate?: string
  vehicleNumber?: string
  poNumber?: string
  items: {
    materialId: string
    orderedQuantity?: number
    receivedQuantity?: number
    acceptedQuantity?: number
    rejectedQuantity?: number
    rate?: number
  }[]
}

/**
 * One delivery, many materials: cement, sand and aggregate on a single challan.
 *
 * <p>Accepted defaults to whatever was received, because full acceptance is the normal
 * case and making the engineer retype it is how "accepted" ends up blank.</p>
 *
 * <p>The same form edits a delivery already recorded: pass {@code initial} and the
 * whole receipt, header and lines together, is replaced by what is saved.</p>
 */
export function MaterialMultiForm({
  pending,
  error,
  initial,
  onCancel,
  onSave,
}: {
  pending: boolean
  error: unknown
  initial?: MaterialReceiptInitial
  onCancel: () => void
  onSave: (body: Record<string, unknown>) => Promise<void>
}) {
  const suppliers = useSuppliers()
  const contractors = useContractors()
  const materials = useMaterials({ page: 0, size: 300 })
  const [header, setHeader] = useState({
    supplierId: initial?.supplierId ?? '',
    supplyScope: (initial?.supplyScope ?? 'OWN') as SupplyScope,
    contractorId: initial?.contractorId ?? '',
    challanNumber: initial?.challanNumber ?? '',
    challanDate: initial?.challanDate ?? new Date().toISOString().slice(0, 10),
    vehicleNumber: initial?.vehicleNumber ?? '',
    poNumber: initial?.poNumber ?? '',
  })
  const [lines, setLines] = useState<Line[]>(
    initial
      ? initial.items.map((i) => ({
          key: nextKey++,
          materialId: i.materialId,
          ordered: i.orderedQuantity != null ? String(i.orderedQuantity) : '',
          received: i.receivedQuantity != null ? String(i.receivedQuantity) : '',
          accepted: i.acceptedQuantity != null ? String(i.acceptedQuantity) : '',
          rejected: i.rejectedQuantity != null ? String(i.rejectedQuantity) : '',
          rate: i.rate != null ? String(i.rate) : '',
        }))
      : [emptyLine(), emptyLine()],
  )

  function update(key: number, field: keyof Line, value: string) {
    setLines((prev) =>
      prev.map((l) => {
        if (l.key !== key) return l
        const next = { ...l, [field]: value }
        // Received implies fully accepted until someone says otherwise.
        if (field === 'received') {
          const rejected = Number(next.rejected || 0)
          next.accepted = String(Math.max(Number(value || 0) - rejected, 0))
        }
        if (field === 'rejected') {
          const received = Number(next.received || 0)
          next.accepted = String(Math.max(received - Number(value || 0), 0))
        }
        return next
      }),
    )
  }

  const materialById = new Map((materials.data?.content ?? []).map((m) => [m.id, m]))
  // A line counts once it names a material and carries a figure — received, or ordered
  // for a delivery that is on its way. Keeping the ordered-only ones matters most when
  // editing: a template fills the order and leaves received blank, and dropping those
  // lines on save would quietly delete the rest of the delivery while somebody typed
  // the quantity for the one lorry that had arrived.
  const filled = lines.filter(
    (l) => l.materialId && (Number(l.received) > 0 || Number(l.ordered) > 0),
  )
  const badSplit = filled.filter(
    (l) => Number(l.accepted || 0) + Number(l.rejected || 0) !== Number(l.received || 0),
  )
  const duplicate = new Set(filled.map((l) => l.materialId)).size !== filled.length
  const scopeMissing = header.supplyScope === 'CONTRACTOR' && !header.contractorId
  const total = filled.reduce((sum, l) => sum + Number(l.accepted || 0) * Number(l.rate || 0), 0)

  return (
    <>
      <DialogTitle>
        {initial ? `Edit delivery ${initial.receiptNumber ?? ''}`.trim() : 'Material received'}
      </DialogTitle>
      <DialogContent dividers>
        {error != null && (
          <Box sx={{ mb: 2 }}>
            <ErrorBlock error={error} title="Could not save the delivery" />
          </Box>
        )}

        {/* Whose material this is decides whose account it lands in, so it is asked first
            and separately from who physically delivered it. */}
        <Box
          sx={{
            display: 'grid',
            gap: 2,
            gridTemplateColumns: {
              xs: '1fr',
              sm: header.supplyScope === 'CONTRACTOR' ? '1fr 2fr' : '1fr',
            },
            p: 1.5,
            mb: 2,
            bgcolor: 'action.hover',
            borderRadius: 1,
          }}
        >
          <TextField
            select
            size="small"
            label="Whose material"
            value={header.supplyScope}
            onChange={(e) => {
              const scope = e.target.value as SupplyScope
              setHeader({
                ...header,
                supplyScope: scope,
                contractorId: scope === 'CONTRACTOR' ? header.contractorId : '',
              })
            }}
          >
            <MenuItem value="OWN">Own procurement</MenuItem>
            <MenuItem value="CONTRACTOR">Contractor supplied</MenuItem>
            <MenuItem value="CLIENT">Client issued</MenuItem>
          </TextField>
          {header.supplyScope === 'CONTRACTOR' && (
            <TextField
              select
              size="small"
              label="Contractor"
              value={header.contractorId}
              error={!header.contractorId}
              helperText="Whose scope of work this material came under"
              onChange={(e) => setHeader({ ...header, contractorId: e.target.value })}
            >
              <MenuItem value="">Pick one</MenuItem>
              {(contractors.data ?? []).map((c) => (
                <MenuItem key={c.id} value={c.id}>
                  {c.name}
                </MenuItem>
              ))}
            </TextField>
          )}
        </Box>

        <Box sx={{ display: 'grid', gap: 2, gridTemplateColumns: { xs: '1fr', sm: '2fr 1fr 1fr' }, mb: 3 }}>
          <TextField
            select
            label="Supplier"
            value={header.supplierId}
            helperText="Who delivered it, whoever's material it is"
            onChange={(e) => setHeader({ ...header, supplierId: e.target.value })}
          >
            <MenuItem value="">Not specified</MenuItem>
            {(suppliers.data ?? []).map((s) => (
              <MenuItem key={s.id} value={s.id}>
                {s.name}
              </MenuItem>
            ))}
          </TextField>
          <TextField
            label="Challan number"
            value={header.challanNumber}
            onChange={(e) => setHeader({ ...header, challanNumber: e.target.value })}
            helperText="Duplicate from the same supplier is refused"
          />
          <TextField
            label="Challan date"
            type="date"
            value={header.challanDate}
            onChange={(e) => setHeader({ ...header, challanDate: e.target.value })}
            slotProps={{ inputLabel: { shrink: true } }}
          />
          <TextField
            label="Vehicle number"
            value={header.vehicleNumber}
            onChange={(e) => setHeader({ ...header, vehicleNumber: e.target.value })}
          />
          <TextField
            label="PO number"
            value={header.poNumber}
            onChange={(e) => setHeader({ ...header, poNumber: e.target.value })}
            sx={{ gridColumn: { sm: 'span 2' } }}
          />
        </Box>

        <Typography variant="subtitle2" gutterBottom>
          Materials on this challan
        </Typography>
        <Box sx={{ display: 'grid', gridTemplateColumns: '3fr 1fr 1fr 1fr 1fr 1fr 40px', gap: 1, mb: 1 }}>
          {['Material', 'Ordered', 'Received', 'Accepted', 'Rejected', 'Rate', ''].map((h) => (
            <Typography key={h} variant="caption" color="text.secondary" sx={{ fontWeight: 700 }}>
              {h}
            </Typography>
          ))}
        </Box>

        {lines.map((line) => {
          const material = materialById.get(line.materialId)
          const split =
            Number(line.accepted || 0) + Number(line.rejected || 0) !== Number(line.received || 0)
          return (
            <Box
              key={line.key}
              sx={{
                display: 'grid',
                gridTemplateColumns: '3fr 1fr 1fr 1fr 1fr 1fr 40px',
                gap: 1,
                mb: 1,
                alignItems: 'center',
              }}
            >
              <TextField
                select
                value={line.materialId}
                onChange={(e) => update(line.key, 'materialId', e.target.value)}
                helperText={material ? `in ${material.uomCode}` : ' '}
              >
                <MenuItem value="">—</MenuItem>
                {(materials.data?.content ?? []).map((m) => (
                  <MenuItem key={m.id} value={m.id}>
                    {m.code} — {m.name}
                  </MenuItem>
                ))}
              </TextField>
              <TextField type="number" value={line.ordered}
                         onChange={(e) => update(line.key, 'ordered', e.target.value)} />
              <TextField type="number" value={line.received}
                         onChange={(e) => update(line.key, 'received', e.target.value)} />
              <TextField type="number" value={line.accepted} error={split}
                         onChange={(e) => update(line.key, 'accepted', e.target.value)} />
              <TextField type="number" value={line.rejected} error={split}
                         onChange={(e) => update(line.key, 'rejected', e.target.value)} />
              <TextField type="number" value={line.rate}
                         onChange={(e) => update(line.key, 'rate', e.target.value)} />
              <IconButton
                size="small"
                onClick={() => setLines((p) => (p.length > 1 ? p.filter((l) => l.key !== line.key) : p))}
                disabled={lines.length === 1}
              >
                <DeleteOutlineIcon fontSize="small" />
              </IconButton>
            </Box>
          )
        })}

        <Button size="small" startIcon={<AddIcon />} onClick={() => setLines((p) => [...p, emptyLine()])}>
          Add material
        </Button>

        {duplicate && (
          <Alert severity="error" sx={{ mt: 2 }}>
            The same material appears twice on this challan.
          </Alert>
        )}
        {badSplit.length > 0 && (
          <Alert severity="error" sx={{ mt: 2 }}>
            {badSplit.length} line(s): accepted plus rejected must equal received.
          </Alert>
        )}
        {scopeMissing && (
          <Alert severity="error" sx={{ mt: 2 }}>
            Contractor-supplied material has to name the contractor.
          </Alert>
        )}
        {filled.length > 0 && badSplit.length === 0 && !duplicate && (
          <Alert severity="success" sx={{ mt: 2 }}>
            {filled.length} material line(s)
            {total > 0 && <> · value ₹ {total.toLocaleString('en-IN')}</>}
          </Alert>
        )}
      </DialogContent>
      <DialogActions>
        <Button onClick={onCancel}>Cancel</Button>
        <Button
          variant="contained"
          disabled={pending || filled.length === 0 || badSplit.length > 0 || duplicate || scopeMissing}
          onClick={() =>
            onSave({
              supplierId: header.supplierId || undefined,
              supplyScope: header.supplyScope,
              contractorId: header.supplyScope === 'CONTRACTOR' ? header.contractorId : undefined,
              challanNumber: header.challanNumber || undefined,
              challanDate: header.challanDate || undefined,
              vehicleNumber: header.vehicleNumber || undefined,
              poNumber: header.poNumber || undefined,
              qualityStatus: filled.some((l) => Number(l.rejected) > 0)
                ? 'PARTIALLY_ACCEPTED'
                : 'ACCEPTED',
              items: filled.map((l) => ({
                materialId: l.materialId,
                orderedQuantity: Number(l.ordered || 0),
                receivedQuantity: Number(l.received),
                acceptedQuantity: Number(l.accepted || 0),
                rejectedQuantity: Number(l.rejected || 0),
                rate: l.rate ? Number(l.rate) : undefined,
              })),
            })
          }
        >
          {pending
            ? 'Saving...'
            : initial
              ? `Save changes (${filled.length})`
              : `Save delivery (${filled.length})`}
        </Button>
      </DialogActions>
    </>
  )
}
